import { useState } from 'react';
import { ReactComponent as ChevronIcon } from 'assets/icons/chevron.svg';
import { clsx } from 'clsx';
import Checkbox from 'components/Checkbox/Checkbox';

import type ICheckboxTreeItem from '../../interfaces/ICheckboxTreeItem';
import type ICheckboxTreeNode from './interfaces/ICheckboxTreeNode';

import styles from '../../scss/CheckboxTree.module.scss';

function CheckboxTreeNode({ item, selectedLeafIds, onToggleItem }: ICheckboxTreeNode) {
  const [isExpanded, setIsExpanded] = useState<boolean>(true);
  const hasChildren = item.children.length > 0;

  const collectLeafIds = (treeItem: ICheckboxTreeItem): string[] => (
    treeItem.children.length > 0
      ? treeItem.children.flatMap((child) => collectLeafIds(child))
      : [treeItem.id]
  );

  const leafIds = collectLeafIds(item);
  const selectedCount = leafIds.filter((leafId) => selectedLeafIds.includes(leafId)).length;
  const isChecked = selectedCount === leafIds.length;
  const showSquare = selectedCount > 0 && !isChecked;

  const handleToggleExpand = () => {
    setIsExpanded((prev) => !prev);
  };

  const handleChangeChecked = () => {
    onToggleItem(item);
  };

  return (
    <div className={styles.node}>
      <div className={styles.row}>
        {hasChildren ? (
          <button
            type="button"
            className={clsx(styles.chevronButton, { [styles.expanded]: isExpanded })}
            onClick={handleToggleExpand}
            aria-label={item.label}
            aria-expanded={isExpanded}
          >
            <ChevronIcon />
          </button>
        ) : (
          <span className={styles.chevronPlaceholder} />
        )}
        <Checkbox
          name={encodeURIComponent(item.id)}
          checked={selectedCount > 0}
          value={String(isChecked)}
          onChange={handleChangeChecked}
          showSquare={showSquare}
          label={<span>{item.label}</span>}
        />
      </div>
      {hasChildren && isExpanded ? (
        <div className={styles.children}>
          {item.children.map((child) => (
            <CheckboxTreeNode
              key={child.id}
              item={child}
              selectedLeafIds={selectedLeafIds}
              onToggleItem={onToggleItem}
            />
          ))}
        </div>
      ) : null}
    </div>
  );
}

export default CheckboxTreeNode;
